import React from "react";
import { Drop, Package } from "@phosphor-icons/react";

/**
 * Compact colored badge for the product format. Signals at-a-glance whether
 * the SKU is a solution reference standard vs a lyophilized reference standard
 * vs a tablet formulation. Never shows a badge for formats we don't recognize.
 */
export function FormBadge({ format, className = "" }) {
  if (!format) return null;

  const f = String(format).toLowerCase();
  let style, icon, label;
  if (f.includes("solution") || f.includes("aqueous")) {
    style = "text-cyan-800 bg-cyan-50 border border-cyan-200";
    icon = <Drop size={9} weight="bold" />;
    label = "SOLUTION";
  } else if (f.includes("tablet") || f.includes("oral")) {
    style = "text-violet-800 bg-violet-50 border border-violet-200";
    icon = <Package size={9} weight="bold" />;
    label = "TABLET";
  } else {
    style = "text-brand-navy bg-brand-surface border border-brand-border";
    icon = <Package size={9} weight="bold" />;
    label = "LYOPHILIZED";
  }
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] rounded-full ${style} ${className}`}
      data-testid="form-badge"
    >
      {icon} {label}
    </span>
  );
}

/**
 * Human-readable "formulation line" for a product. Emits the exact phrasing
 * required for unambiguous variant identification:
 *   - `Solution Reference Standard — 10 mL`
 *   - `Lyophilized Reference Standard — 10 mg`
 *   - `Oral Tablet Reference Standard — 5 mg`
 *
 * Falls back to strength + raw format when the mapping is unknown.
 */
export function formulationLine(product) {
  const strength = product.strength || "";
  const raw = String(product.format || "").toLowerCase();
  if (raw.includes("solution") || raw.includes("aqueous")) {
    return `Solution Reference Standard — ${strength}`;
  }
  if (raw.includes("tablet") || raw.includes("oral")) {
    return `Oral Tablet Reference Standard — ${strength}`;
  }
  if (raw.includes("lyophilized") || raw.includes("powder")) {
    return `Lyophilized Reference Standard — ${strength}`;
  }
  return strength && product.format ? `${strength} · ${product.format}` : strength;
}
